<script setup>
import TableViewProduct from './TableViewProduct.vue';
import SortingWidget from '../Category/SortingWidget.vue';
import BrandDisplay from '@/Components/Product/BrandDisplay.vue';
import { computed } from 'vue';

const props = defineProps({
    products: {
        type: Array,
        required: true,
    },
    loadNextPage: {
        type: Function,
        required: true,
    },
    loadPrevPage: {
        type: Function,
        required: true,
    },
    loadSpecificPage: {
        type: Function,
        required: true,
    },
    selectedSort: {
        type: String
    },
    currentPage: {
        type: Number,
        default: 1
    },
    totalPages: {
        type: Number,
        default: 1
    },
});

const emit = defineEmits(['sortingUpdated']);

const sortingUpdated = (key) => {
    emit('sortingUpdated', key);
};

// Generate page numbers for pagination with ellipsis
const pageNumbers = computed(() => {
    if (props.totalPages <= 7) {
        // Show all pages if there are 7 or fewer
        return Array.from({ length: props.totalPages }, (_, i) => i + 1);
    }
    
    // Always show first, last, current, and some surrounding pages
    const pages = [];
    
    // Always include page 1
    pages.push(1);
    
    // Add ellipsis if current page is more than 3
    if (props.currentPage > 3) {
        pages.push('...');
    }
    
    // Add pages around current page
    const start = Math.max(2, props.currentPage - 1);
    const end = Math.min(props.totalPages - 1, props.currentPage + 1);
    
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }
    
    // Add ellipsis if current page is less than last page - 2
    if (props.currentPage < props.totalPages - 2) {
        pages.push('...');
    }
    
    // Always include last page if more than 1 page
    if (props.totalPages > 1) {
        pages.push(props.totalPages);
    }
    
    return pages;
});

</script>

<template>
    <div class="product-table">
        <table class="table table-striped">
            <thead>
                <tr>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td class="text-end">
                        <SortingWidget @sortingUpdated="sortingUpdated" :selectedSort="selectedSort" />
                    </td>
                </tr>
                <TableViewProduct v-for="product in products" :key="product.id" :product="product" />
                <tr>
                    <td>
                        <nav aria-label="Product pagination" class="mt-3">
                            <div class="pagination-container d-flex justify-content-between align-items-center">
                                <div class="page-info">
                                    Page {{ currentPage }} of {{ totalPages }}
                                </div>
                                <ul class="pagination">
                                    <li class="page-item" :class="{ 'disabled': currentPage <= 1 }">
                                        <a class="page-link" href="#" @click.prevent="loadPrevPage" aria-label="Previous">
                                            <span aria-hidden="true">&laquo;</span>
                                        </a>
                                    </li>
                                    
                                    <li v-for="page in pageNumbers" :key="page" class="page-item" 
                                        :class="{ 'active': page === currentPage, 'disabled': page === '...' }">
                                        <template v-if="page === '...'">
                                            <span class="page-link">{{ page }}</span>
                                        </template>
                                        <template v-else>
                                            <a class="page-link" href="#" @click.prevent="loadSpecificPage(page)">{{ page }}</a>
                                        </template>
                                    </li>
                                    
                                    <li class="page-item" :class="{ 'disabled': currentPage >= totalPages }">
                                        <a class="page-link" href="#" @click.prevent="loadNextPage" aria-label="Next">
                                            <span aria-hidden="true">&raquo;</span>
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </nav>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.product-table {
    /* Add styling for the product table here */
}

.pagination-container {
    width: 100%;
}

.page-info {
    color: #6c757d;
    font-size: 0.9rem;
}

.page-link {
    color: #0d6efd;
    background-color: #fff;
    border: 1px solid #dee2e6;
}

.page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
}

.page-item.disabled .page-link {
    color: #6c757d;
    pointer-events: none;
    background-color: #fff;
    border-color: #dee2e6;
}
</style>